import * as React from 'react'
import {
  isComponentBlock,
  type Block,
  type ListBlock,
  ComponentBlock,
  ListView,
  BlockType,
  MediaBlock,
} from '@basehub/data/replicache/index'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import * as PropertiesPanel from '../../../properties-panel'
import { getId } from '@basehub/utils/id/index'
import { DocumentSidebarPortal } from '../../../document-sidebar-portal'
import { SelectorResultBlock } from '~/sotano-kit/primitives/selector'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { TreeNode } from '@basehub/utils/tree/index'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { BlockIcon } from '~/components/block-icon'
import { useSelectedView } from '../views/use-selected-view'
import { LetterCaseCapitalizeIcon, TrashIcon } from '@radix-ui/react-icons'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { ListBlockViewIcon } from '../views/view-switcher'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { swapIndexes } from '../helpers/sort'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getViewDefaultTitle } from '@basehub/data/replicache/schema/block/list/utils'
import {
  tableViewSchema,
  galleryViewSchema,
} from '@basehub/data/replicache/index'
import { useEditorStore } from '../../../editor-store'
import { useRepoStore } from '../../../repo-store'
import uniqBy from 'lodash.uniqby'
import { resolveColumnsMetadata } from '../views/table/columns'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import {
  UpdateListViewParams,
  ChangeListTemplateParams,
  DeleteListViewParams,
  ResetListTemplateParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/list'
import { useTabsStore } from '../../../tabs/store'
import { useEditorMode } from '../../../editor-mode-provider'
import { useTreeSlice } from '~/hooks/use-tree-slice'

export const ListBlockPropertiesPanel = ({
  block,
  path,
}: {
  block: ListBlock
  parent: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const [selectedView, updateSelectedView] = useSelectedView(block)
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const setFocus = useRepoStore((s) => s.setFocus)
  const undoManager = useRepoUndoManager()
  const { encodedSegment } = useBranchOrCommitSegment()
  const editorMode = useEditorMode()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string | undefined) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const targetBlockId_withinTree: string | undefined = useTreeSlice(
    blockTreeManager,
    (tm) => {
      const targetBlock = tm.getNode(block.typeOptions.targetBlockId, {
        required: true,
      })
      return targetBlock.value.id
    },
    'reference',
    [block.typeOptions.targetBlockId]
  )

  const template = useTreeSlice(
    blockTreeManager,
    (tm) => {
      const template = tm.getNode(block.typeOptions.targetBlockId, {
        fallbackKey: 'sourceBlockId',
      })
      if (!template || !isComponentBlock(template.value)) {
        return null
      }

      return template as TreeNode<ComponentBlock>
    },
    'reference',
    [block.typeOptions.targetBlockId]
  )

  const templateDisplayData = React.useMemo(() => {
    if (!template?.parentKey || !blockTreeManager) return null
    const parentNode = blockTreeManager.getNode(template.parentKey)

    const displayData = getBlockDisplayData({
      block: template.value,
      parentBlock: parentNode?.value,
    })

    return displayData
  }, [template, blockTreeManager])

  const componentOptions = useTreeSlice(blockTreeManager, (tm) => {
    const _componentOptions: PropertiesPanel.PropertiesPanelComboboxOption[] =
      []
    if (!tm) return _componentOptions
    for (const cId of (tm.compoundIndexes.get('components') ??
      new Set()) as Set<string>) {
      const node = tm?.getNode(cId)
      if (!node) continue
      if (!isComponentBlock(node.value)) continue

      const parentNode = node.parentKey
        ? tm?.getNode(node.parentKey)
        : undefined

      const displayData = getBlockDisplayData({
        block: node.value,
        parentBlock: parentNode?.value,
      })

      _componentOptions.push({
        label: displayData.displayTitle,
        value: idToSourceBlockId(node.value),
      })
    }

    return _componentOptions
  })

  const isTargetNestedBelow = template?.parentKey === block.id

  type ToggleColumnOption = PropertiesPanel.CheckItem & {
    type: BlockType
    width: number
  }
  const viewColumnsMetadata: {
    columnsMetadata: ListView['columnsMetadata']
    options: ToggleColumnOption[]
    checkedOptions: ToggleColumnOption[]
  } = React.useMemo(() => {
    if (!blockTreeManager || !template) {
      return { columnsMetadata: [], options: [], checkedOptions: [] }
    }

    const resolvedOptions = resolveColumnsMetadata({
      selectedView,
      listTemplateId: template.value.id,
      treeManager: blockTreeManager,
    })

    const checkedOptions: ToggleColumnOption[] = []
    const options: ToggleColumnOption[] = resolvedOptions
      .map((cm) => {
        if (cm.blockId === '__title') {
          const titleOption = {
            id: '__title',
            name: 'Title',
            icon: <LetterCaseCapitalizeIcon />,
            type: 'text' as const,
            width: cm.width,
            disabled: true,
          }

          checkedOptions.push(titleOption)
          return titleOption
        }

        const blockForColumn = blockTreeManager.getNode(cm.blockId)?.value
        if (!blockForColumn) return false

        const option = {
          id: cm.blockId,
          name: blockForColumn.title || 'Untitled',
          icon: <BlockIcon block={blockForColumn} />,
          type: blockForColumn.type,
          width: cm.width,
        }

        if (!cm.hidden) checkedOptions.push(option)

        return option
      })
      .filter(Boolean)

    return { columnsMetadata: resolvedOptions, options, checkedOptions }
  }, [blockTreeManager, selectedView, template])

  const thumbnailOptions = React.useMemo(() => {
    if (
      selectedView?.layout !== 'gallery' &&
      selectedView?.layout !== 'inline'
    ) {
      return []
    }
    const options: { id: string; label: string }[] = []

    viewColumnsMetadata.options.forEach((cm) => {
      if (selectedView?.layout === 'gallery') {
        if (cm.type === 'media') {
          const mediaBlock = blockTreeManager?.getNode(cm.id)
            ?.value as MediaBlock | null
          if (
            mediaBlock?.constraints.allowedMimeTypes?.image ||
            mediaBlock?.constraints.allowedMimeTypes?.video
          ) {
            options.push({ id: cm.id, label: cm.name || 'Untitled' })
          }
        }
      } else {
        if (['text', 'rich-text', 'number'].includes(cm.type)) {
          options.push({ id: cm.id, label: cm.name || 'Untitled' })
        }
      }
    })

    return options
  }, [blockTreeManager, selectedView?.layout, viewColumnsMetadata.options])

  let thumbnail: string | undefined
  if (selectedView && 'thumbnailColumn' in selectedView) {
    thumbnail = selectedView.thumbnailColumn
  } else if (selectedView?.layout === 'inline') {
    thumbnail = '__title'
  }

  const handleClear = React.useCallback(async () => {
    if (!replicache || !targetBlockId_withinTree) return

    const params: UpdateListViewParams = {
      id: selectedView?.id,
      blockId: block.id,
      targetBlockId_withinTree,
      idSeed: getId(),
      data: {
        columnsMetadata: viewColumnsMetadata.options.map((o) => {
          let isThumbnail = false
          if (selectedView && 'thumbnailColumn' in selectedView) {
            if (o.id === selectedView.thumbnailColumn) {
              isThumbnail = true
            }
          }

          const isTitleAndIsHidden =
            o.id === '__title' &&
            viewColumnsMetadata.checkedOptions.some((co) => co.id === '__title')

          return {
            blockId: o.id,
            width: o.width,
            hidden: !isTitleAndIsHidden && !isThumbnail,
          }
        }),
      },
      path,
      isoNow: new Date().toISOString(),
    }
    const result = await replicache.mutate.updateListView(params)
    if (result.undo) {
      undoManager.current?.push({
        redo: [
          getInputFocusOp(
            `${sourceBlockId}-${selectedView?.id ?? 'default'}-pp-columns`
          ),
          { op: 'updateListView', args: params },
        ],
        undo: [
          getInputFocusOp(
            `${sourceBlockId}-${selectedView?.id ?? 'default'}-pp-columns`
          ),
          { op: 'blockBatchMutations', args: { mutations: result.undo } },
        ],
      })
    }
  }, [
    block.id,
    getInputFocusOp,
    path,
    replicache,
    selectedView,
    sourceBlockId,
    targetBlockId_withinTree,
    undoManager,
    viewColumnsMetadata.checkedOptions,
    viewColumnsMetadata.options,
  ])

  const handleSelectAll = React.useCallback(async () => {
    if (!replicache || !targetBlockId_withinTree) return

    const params: UpdateListViewParams = {
      id: selectedView?.id,
      blockId: block.id,
      targetBlockId_withinTree,
      idSeed: getId(),
      data: {
        columnsMetadata: viewColumnsMetadata.options.map((o) => {
          return {
            blockId: o.id,
            width: o.width,
            hidden: false,
          }
        }),
      },
      path,
      isoNow: new Date().toISOString(),
    }
    const result = await replicache.mutate.updateListView(params)
    if (result.undo) {
      undoManager.current?.push({
        redo: [
          getInputFocusOp(
            `${sourceBlockId}-${selectedView?.id ?? 'default'}-pp-columns`
          ),
          { op: 'updateListView', args: params },
        ],
        undo: [
          getInputFocusOp(
            `${sourceBlockId}-${selectedView?.id ?? 'default'}-pp-columns`
          ),
          { op: 'blockBatchMutations', args: { mutations: result.undo } },
        ],
      })
    }
  }, [
    block.id,
    getInputFocusOp,
    path,
    replicache,
    selectedView?.id,
    sourceBlockId,
    targetBlockId_withinTree,
    undoManager,
    viewColumnsMetadata.options,
  ])

  return (
    <PropertiesPanel.PropertiesContainer>
      <PropertiesPanel.Group title="Template">
        <PropertiesPanel.SelectorOption<PropertiesPanel.PropertiesPanelComboboxOption>
          emptyStateLabel="No components to target."
          selected={
            template && templateDisplayData
              ? {
                  label: templateDisplayData.displayTitle,
                  value: idToSourceBlockId(template.value),
                }
              : undefined
          }
          id={`el-${sourceBlockId}-pp-template`}
          onSelect={async (value) => {
            if (value.value === block.typeOptions.targetBlockId) return // same team

            if (value.value === 'reset') {
              const params: ResetListTemplateParams = {
                id: block.id,
                idSeed: getId(),
                isoNow: new Date().toISOString(),
                path,
              }
              const result = await replicache?.mutate.resetListTemplate(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-template`),
                    { op: 'resetListTemplate', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-template`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            } else {
              const params: ChangeListTemplateParams = {
                id: block.id,
                newTargetId: value.value,
                idSeed: getId(),
                isoNow: new Date().toISOString(),
                path,
              }
              const result = await replicache?.mutate.changeListTemplate(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-template`),
                    { op: 'changeListTemplate', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-template`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }
          }}
          renderResult={(search) => (
            <SelectorResultBlock
              onSelect={() => undefined}
              search={search}
              multiple={false}
              teamSlug={teamSlug}
              repoSlug={repoSlug}
              repoStore={{ setFocus }}
              targetBlocks={
                template && templateDisplayData
                  ? [
                      {
                        ...template.value,
                        title: templateDisplayData.displayTitle,
                      },
                    ]
                  : []
              }
            />
          )}
          options={() => {
            const preOptions: PropertiesPanel.PropertiesPanelComboboxOption[] =
              []
            if (!isTargetNestedBelow) {
              preOptions.push({
                label: 'Reset template',
                value: 'reset',
                addNew: true,
                postSeparator: true,
              })
            }
            return [
              ...preOptions,
              ...(componentOptions?.sort((a) => {
                if (template && a.value === idToSourceBlockId(template.value)) {
                  return -1
                }
                return 0
              }) ?? []),
            ]
          }}
        />
      </PropertiesPanel.Group>
      <PropertiesPanel.Group title="Constraints" spacing="tight">
        <PropertiesPanel.LimitInput
          name="Limit"
          value={{
            min: block.constraints.minRows ?? null,
            max: block.constraints.maxRows ?? null,
          }}
          minId={`el-${sourceBlockId}-pp-min-rows`}
          maxId={`el-${sourceBlockId}-pp-max-rows`}
          max={999_999}
          mutateMax={async (newValue) => {
            const parsed = newValue ? Math.round(Math.max(newValue, 0)) : null
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                type: block.type,
                constraints: {
                  maxRows: parsed,
                },
              },
              strategy: 'merge-deep',
              path,
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-max-rows`),
                  { op: 'updateBlock', args: params },
                  {
                    op: 'customWindowEvent',
                    args: {
                      name: `${sourceBlockId}-pp-max-rows`,
                      detail: { action: 'sync' },
                    },
                  },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-max-rows`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                  {
                    op: 'customWindowEvent',
                    args: {
                      name: `${sourceBlockId}-pp-max-rows`,
                      detail: { action: 'sync' },
                    },
                  },
                ],
              })
            }
          }}
          mutateMin={async (newValue) => {
            const parsed = newValue ? Math.max(newValue, 0) : null
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                type: block.type,
                constraints: { minRows: parsed },
              },
              strategy: 'merge-deep',
              path,
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-min-rows`),
                  { op: 'updateBlock', args: params },
                  {
                    op: 'customWindowEvent',
                    args: {
                      name: `${sourceBlockId}-pp-min-rows`,
                      detail: { action: 'sync' },
                    },
                  },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-min-rows`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                  {
                    op: 'customWindowEvent',
                    args: {
                      name: `${sourceBlockId}-pp-min-rows`,
                      detail: { action: 'sync' },
                    },
                  },
                ],
              })
            }
          }}
        />
      </PropertiesPanel.Group>

      <PropertiesPanel.ConfigureContainer block={block}>
        <PropertiesPanel.Group title="Variants">
          <div className="overflow-hidden cursor-default text-dark-muted text-pretty">
            Editable in{' '}
            <ButtonLink
              className="underline transition-colors hover:text-dark-dim"
              href={
                template
                  ? `/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${idToSourceBlockId(
                      template.value
                    )}`
                  : '#'
              }
              unstyled
              focusStyled
            >
              template component
            </ButtonLink>
            .
          </div>
        </PropertiesPanel.Group>
      </PropertiesPanel.ConfigureContainer>

      <PropertiesPanel.Group title="Selected view" spacing="loose">
        <PropertiesPanel.TextOption
          name="Title"
          id={`el-${sourceBlockId}-${
            selectedView?.id ?? 'default'
          }-pp-view-title`}
          placeholder="Name your view"
          value={selectedView?.title || getViewDefaultTitle(selectedView)}
          mutate={async (v) => {
            if (!replicache || !targetBlockId_withinTree) return
            const params: UpdateListViewParams = {
              id: selectedView?.id,
              idSeed: getId(),
              blockId: block.id,
              targetBlockId_withinTree,
              data: { title: v },
              path,
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateListView(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(
                    `${sourceBlockId}-${
                      selectedView?.id ?? 'default'
                    }-pp-view-title`
                  ),
                  { op: 'updateListView', args: params },
                  {
                    op: 'customWindowEvent',
                    args: {
                      name: `${sourceBlockId}-${
                        selectedView?.id ?? 'default'
                      }-pp-view-title`,
                      detail: { action: 'sync' },
                    },
                  },
                ],
                undo: [
                  getInputFocusOp(
                    `${sourceBlockId}-${
                      selectedView?.id ?? 'default'
                    }-pp-view-title`
                  ),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                  {
                    op: 'customWindowEvent',
                    args: {
                      name: `${sourceBlockId}-${
                        selectedView?.id ?? 'default'
                      }-pp-view-title`,
                      detail: { action: 'sync' },
                    },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.LayoutOption
          id={`el-${sourceBlockId}-${selectedView?.id ?? 'default'}-pp-layout`}
          title="Layout"
          options={[
            {
              id: 'table',
              title: 'Table',
              icon: (
                <ListBlockViewIcon
                  view={{ layout: 'table' }}
                  iconProps={{ width: 18, height: 18 }}
                />
              ),
            },
            {
              id: 'gallery',
              title: 'Gallery',
              icon: (
                <ListBlockViewIcon
                  view={{ layout: 'gallery' }}
                  iconProps={{ width: 18, height: 18 }}
                />
              ),
            },
            {
              id: 'inline',
              title: 'Inline',
              icon: (
                <ListBlockViewIcon
                  view={{ layout: 'inline' }}
                  iconProps={{ width: 18, height: 18 }}
                />
              ),
            },
          ]}
          value={selectedView?.layout ?? 'table'}
          onValueChange={async (v) => {
            if (!replicache || !targetBlockId_withinTree) return

            const params: UpdateListViewParams = {
              id: selectedView?.id,
              blockId: block.id,
              targetBlockId_withinTree,
              idSeed: getId(),
              data: { layout: v.id as ListView['layout'] },
              path,
              isoNow: new Date().toISOString(),
            }
            const result = await replicache.mutate.updateListView(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(
                    `${sourceBlockId}-${
                      selectedView?.id ?? 'default'
                    }-pp-layout`
                  ),
                  { op: 'updateListView', args: params },
                ],
                undo: [
                  getInputFocusOp(
                    `${sourceBlockId}-${
                      selectedView?.id ?? 'default'
                    }-pp-layout`
                  ),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        {selectedView && selectedView.layout !== 'table' && (
          <PropertiesPanel.SelectOption
            id={`el-${sourceBlockId}-${
              selectedView?.id ?? 'default'
            }-pp-thumbnail`}
            title={
              selectedView.layout === 'inline'
                ? 'Display title'
                : 'Card preview'
            }
            options={thumbnailOptions}
            value={thumbnail}
            disabled={!thumbnailOptions.length}
            placeholder={
              thumbnailOptions.length
                ? 'Choose preview'
                : selectedView.layout === 'gallery'
                  ? 'No media fields found'
                  : 'No preview fields available'
            }
            handleValueChange={async (value) => {
              if (!replicache || !targetBlockId_withinTree) return

              const parsedValue = galleryViewSchema
                .pick({ thumbnailColumn: true })
                .safeParse({ thumbnailColumn: value })

              if (!parsedValue.success) return

              const params: UpdateListViewParams = {
                id: selectedView?.id,
                blockId: block.id,
                targetBlockId_withinTree,
                idSeed: getId(),
                data: {
                  thumbnailColumn: parsedValue.data.thumbnailColumn,
                } as Partial<ListView>,
                path,
                isoNow: new Date().toISOString(),
              }
              const result = await replicache.mutate.updateListView(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(
                      `${sourceBlockId}-${
                        selectedView?.id ?? 'default'
                      }-pp-thumbnail`
                    ),
                    { op: 'updateListView', args: params },
                  ],
                  undo: [
                    getInputFocusOp(
                      `${sourceBlockId}-${
                        selectedView?.id ?? 'default'
                      }-pp-thumbnail`
                    ),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
          />
        )}
        <PropertiesPanel.SelectOption
          title="Show on first load"
          id={`el-${sourceBlockId}-${
            selectedView?.id ?? 'default'
          }-pp-show-on-first-load`}
          options={[
            { id: '10', label: '10' },
            { id: '25', label: '25' },
            { id: '50', label: '50' },
            { id: '100', label: '100' },
          ]}
          value={selectedView?.showOnFirstLoad.toString() ?? '10'}
          handleValueChange={async (value) => {
            if (!replicache || !targetBlockId_withinTree) return

            const parsedValue = tableViewSchema
              .pick({ showOnFirstLoad: true })
              .safeParse({ showOnFirstLoad: parseInt(value) })

            if (!parsedValue.success) return

            const params: UpdateListViewParams = {
              id: selectedView?.id,
              blockId: block.id,
              targetBlockId_withinTree,
              idSeed: getId(),
              data: { showOnFirstLoad: parsedValue.data.showOnFirstLoad },
              path,
              isoNow: new Date().toISOString(),
            }
            const result = await replicache.mutate.updateListView(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(
                    `${sourceBlockId}-${
                      selectedView?.id ?? 'default'
                    }-pp-show-on-first-load`
                  ),
                  { op: 'updateListView', args: params },
                ],
                undo: [
                  getInputFocusOp(
                    `${sourceBlockId}-${
                      selectedView?.id ?? 'default'
                    }-pp-show-on-first-load`
                  ),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        {selectedView?.layout !== 'inline' && (
          <PropertiesPanel.CheckList
            itemName="Columns"
            id={`el-${sourceBlockId}-${
              selectedView?.id ?? 'default'
            }-pp-columns`}
            popoverConfig={{
              prefix: '',
              allowedLabel: 'Shown',
              notAllowedLabel: 'Not shown',
            }}
            title={`Show in ${selectedView?.layout || 'table'}`}
            options={viewColumnsMetadata.options}
            checkedOptions={viewColumnsMetadata.checkedOptions}
            dnd
            dndItemList={viewColumnsMetadata.options.map(({ id }) => id)}
            onDragEnd={async ({ active, over }) => {
              if (
                !blockTreeManager ||
                !selectedView ||
                !targetBlockId_withinTree
              ) {
                return
              }

              if (over && active.id !== over?.id) {
                const activeIndex = viewColumnsMetadata.options.findIndex(
                  ({ id }) => id === active.id
                )
                const overIndex = viewColumnsMetadata.options.findIndex(
                  ({ id }) => id === over.id
                )
                const columnsMetadataById = new Map(
                  selectedView.columnsMetadata.map((cm) => [cm.blockId, cm])
                )

                const reorderedValue = (
                  swapIndexes(
                    [...viewColumnsMetadata.options],
                    activeIndex,
                    overIndex
                  ) ?? []
                )
                  .map((o) => {
                    const found = columnsMetadataById.get(o.id)
                    if (!found) {
                      return {
                        blockId: o.id,
                        width: o.width,
                        hidden: false,
                      }
                    }
                    return {
                      blockId: o.id,
                      width: found.width,
                      hidden: found.hidden,
                    }
                  })
                  .filter(Boolean) satisfies ListView['columnsMetadata']
                if (!reorderedValue) return

                const params: UpdateListViewParams = {
                  blockId: block.id,
                  targetBlockId_withinTree,
                  id: selectedView?.id,
                  data: { columnsMetadata: reorderedValue },
                  idSeed: getId(),
                  path,
                  isoNow: new Date().toISOString(),
                }
                const result = await replicache?.mutate.updateListView(params)
                if (result?.undo) {
                  undoManager.current?.push({
                    redo: [
                      getInputFocusOp(
                        `${sourceBlockId}-${
                          selectedView?.id ?? 'default'
                        }-pp-columns`
                      ),
                      { op: 'updateListView', args: params },
                      {
                        op: 'customWindowEvent',
                        args: {
                          name: `${sourceBlockId}-${
                            selectedView?.id ?? 'default'
                          }-pp-columns`,
                          detail: { action: 'sync' },
                        },
                      },
                    ],
                    undo: [
                      getInputFocusOp(
                        `${sourceBlockId}-${
                          selectedView?.id ?? 'default'
                        }-pp-columns`
                      ),
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                      {
                        op: 'customWindowEvent',
                        args: {
                          name: `${sourceBlockId}-${
                            selectedView?.id ?? 'default'
                          }-pp-columns`,
                          detail: { action: 'sync' },
                        },
                      },
                    ],
                  })
                }
              }
            }}
            mutate={async (newValue, checkOptions) => {
              if (!replicache || !targetBlockId_withinTree) return
              if (newValue === 'overwrite') {
                return checkOptions.length ? handleSelectAll() : handleClear()
              }

              const newColumnsMetadata =
                viewColumnsMetadata.columnsMetadata.map((o) => {
                  let isThumbnail = false
                  if (selectedView && 'thumbnailColumn' in selectedView) {
                    if (o.blockId === selectedView.thumbnailColumn) {
                      isThumbnail = true
                    }
                  }

                  if (isThumbnail) {
                    return {
                      blockId: o.blockId,
                      width: o.width,
                      hidden: false,
                    }
                  }

                  const shouldChange = checkOptions.some(
                    (co) => co.id === o.blockId
                  )

                  if (shouldChange) {
                    return {
                      blockId: o.blockId,
                      width: o.width,
                      hidden: !o.hidden,
                    }
                  }

                  return {
                    blockId: o.blockId,
                    width: o.width,
                    hidden: o.hidden,
                  }
                })

              const cleanedUp = newColumnsMetadata.map((c) => {
                const node = blockTreeManager?.getNode(c.blockId)
                if (!node) return c
                if (!node.value.sourceBlockId) return c
                if (node.value.sourceBlockId === c.blockId) return c
                return { ...c, blockId: node.value.sourceBlockId }
              })

              const params: UpdateListViewParams = {
                id: selectedView?.id,
                blockId: block.id,
                targetBlockId_withinTree,
                idSeed: getId(),
                data: { columnsMetadata: uniqBy(cleanedUp, 'blockId') },
                path,
                isoNow: new Date().toISOString(),
              }
              const result = await replicache.mutate.updateListView(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(
                      `${sourceBlockId}-${
                        selectedView?.id ?? 'default'
                      }-pp-columns`
                    ),
                    { op: 'updateListView', args: params },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-${
                          selectedView?.id ?? 'default'
                        }-pp-columns`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                  undo: [
                    getInputFocusOp(
                      `${sourceBlockId}-${
                        selectedView?.id ?? 'default'
                      }-pp-columns`
                    ),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-${
                          selectedView?.id ?? 'default'
                        }-pp-columns`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                })
              }
            }}
          />
        )}
        {editorMode === 'write:all' && (
          <SimpleTooltip
            content="This is the only view for this list, it can not be deleted"
            disabled={(block.value.views?.length ?? 0) > 1}
          >
            <Button
              intent="secondary"
              id={`el-${sourceBlockId}-${
                selectedView?.id ?? 'default'
              }-pp-delete-view`}
              className="text-red-elevated"
              disabled={!block.value.views || block.value.views?.length < 2}
              onClick={async () => {
                if (block.value.views?.length === 1) return
                if (!replicache || !targetBlockId_withinTree) return

                const params: DeleteListViewParams = {
                  id: selectedView?.id,
                  blockId: block.id,
                  path,
                  isoNow: new Date().toISOString(),
                }
                const result = await replicache.mutate.deleteListView(params)

                if (result?.undo) {
                  undoManager.current?.push({
                    redo: [
                      getInputFocusOp(
                        `${sourceBlockId}-${
                          selectedView?.id ?? 'default'
                        }-pp-delete-view`
                      ),
                      { op: 'deleteListView', args: params },
                    ],
                    undo: [
                      getInputFocusOp(undefined),
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                    ],
                  })
                }

                if (!block.value.views) return

                const indexOfSelectedView = block.value.views.findIndex(
                  (v) => v.id === selectedView?.id
                )

                const prevView = block.value.views[indexOfSelectedView - 1]
                if (prevView) return await updateSelectedView(prevView.id)
                const nextView = block.value.views[indexOfSelectedView + 1]
                if (nextView) return await updateSelectedView(nextView.id)
              }}
              icon={<TrashIcon />}
            >
              Remove view
            </Button>
          </SimpleTooltip>
        )}
      </PropertiesPanel.Group>
    </PropertiesPanel.PropertiesContainer>
  )
}

export const FullViewPropertiesPanelWrapper = ({
  block,
  path,
  children,
  parentBlock,
}: {
  block: ListBlock
  path: string
  children: React.ReactNode
  parentBlock: Block | null
}) => {
  const isSelected = useEditorStore(
    (s) => !s.selectedBlockId || s.selectedBlockId === idToSourceBlockId(block)
  )

  return (
    <DocumentSidebarPortal hidden={!isSelected}>
      <PropertiesPanel.Root block={block} parentBlock={parentBlock}>
        <PropertiesPanel.BlockTitleGroup block={block} path={path} />
        {children}
      </PropertiesPanel.Root>
    </DocumentSidebarPortal>
  )
}
